import { money } from "./money";

/** Machine-readable invoice payload (Scope B worksheet → JSON file). */
export type UgcInvoiceDataRecord = {
  format: "ugc-timeshare-reimbursement-invoice-data";
  format_version: 1;
  generated_at: string;
  operator_legal_name: "Utah Gas Corp";
  aircraft_id: string;
  time_sharee_remitter: string;
  invoice_document_number: string;
  /** YYYY-MM-DD — same as plain-text “Date:” line */
  invoice_date: string;
  /** Timeshare Scope B / Form 720 date range label */
  scope_b_period_label: string;
  personal_classification_basis: string;
  worksheet_legs_in_scope_all: number;
  worksheet_personal_trips: number;
  line_items_usd: {
    federal_air_transportation_taxes_reimbursable_irs_26_27: number;
    fuel_cost_2x_agreement_2_3_j: number;
    total_due_to_operator: number;
  };
};

export type UgcInvoiceDraftInput = {
  aircraftId: string;
  /** Form 720 / Scope B period label */
  periodLabel: string;
  invoiceDocumentNumber: string;
  /** YYYY-MM-DD */
  issuedDateIso: string;
  /** How “personal” is determined (short label) */
  personalBasisLabel: string;
  personalTripsInScope: number;
  legsInScopeTotal: number;
  airExciseReimburse: number;
  fuel2x: number;
  /** Shown in letterhead, e.g. time-sharee name */
  remitterLine: string;
};

/** When the user leaves “document #” blank, default to `{tail}-TS-{invoice date}`. */
export function derivedInvoiceDocumentNumber(
  aircraftId: string,
  issuedDateIso: string
): string {
  const tail = aircraftId.trim().toUpperCase() || "N426DH";
  const d = issuedDateIso.trim();
  return `${tail}-TS-${d}`;
}

export function effectiveInvoiceDocumentNumber(
  aircraftId: string,
  issuedDateIso: string,
  userDocumentNumber: string
): string {
  const t = userDocumentNumber.trim();
  if (t) return t;
  return derivedInvoiceDocumentNumber(aircraftId, issuedDateIso);
}

export function buildUgcInvoiceDataRecord(
  i: UgcInvoiceDraftInput
): UgcInvoiceDataRecord {
  const total = money(i.airExciseReimburse + i.fuel2x);
  const doc = effectiveInvoiceDocumentNumber(
    i.aircraftId,
    i.issuedDateIso,
    i.invoiceDocumentNumber
  );
  return {
    format: "ugc-timeshare-reimbursement-invoice-data",
    format_version: 1,
    generated_at: new Date().toISOString(),
    operator_legal_name: "Utah Gas Corp",
    aircraft_id: i.aircraftId,
    time_sharee_remitter: i.remitterLine,
    invoice_document_number: doc,
    invoice_date: i.issuedDateIso,
    scope_b_period_label: i.periodLabel,
    personal_classification_basis: i.personalBasisLabel,
    worksheet_legs_in_scope_all: i.legsInScopeTotal,
    worksheet_personal_trips: i.personalTripsInScope,
    line_items_usd: {
      federal_air_transportation_taxes_reimbursable_irs_26_27: money(
        i.airExciseReimburse
      ),
      fuel_cost_2x_agreement_2_3_j: money(i.fuel2x),
      total_due_to_operator: total,
    },
  };
}

export function buildUgcInvoiceDataJson(i: UgcInvoiceDraftInput): string {
  return JSON.stringify(buildUgcInvoiceDataRecord(i), null, 2);
}

function fmtUsd(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function buildUgcInvoicePlainText(i: UgcInvoiceDraftInput): string {
  const total = money(i.airExciseReimburse + i.fuel2x);
  const doc = effectiveInvoiceDocumentNumber(
    i.aircraftId,
    i.issuedDateIso,
    i.invoiceDocumentNumber
  );
  const lines = [
    "REIMBURSEMENT INVOICE (DRAFT) — Aircraft time sharing agreement",
    `Utah Gas Corp · ${i.aircraftId}`,
    "",
    `From: ${i.remitterLine}`,
    "",
    `Document no.: ${doc}`,
    `Date: ${i.issuedDateIso}`,
    `Period covered (Timeshare Scope B / Form 720 date range): ${i.periodLabel}`,
    "",
    `Worksheet legs in date range: ${i.legsInScopeTotal} · Personal trips (per “${i.personalBasisLabel}”): ${i.personalTripsInScope}`,
    "",
    "Line items — personal legs only (worksheet model):",
    `  Federal air transportation taxes reimbursable to operator (IRS Nos. 26+27 allocable)    ${fmtUsd(i.airExciseReimburse)}`,
    `  2× fuel cost (agreement §2.3(j)-style, where priced on legs)                        ${fmtUsd(i.fuel2x)}`,
    `  ${"—".repeat(72)}`,
    `  Total due to Utah Gas Corp                                                          ${fmtUsd(total)}`,
    "",
    "Notes:",
    "- Worksheet estimate only — not a filed return. Match totals on Timeshare → Scope B before sending.",
    "- This app does not send email; use your mail client or paste into Utah Gas’s billing process.",
    "",
  ];
  return lines.join("\n");
}

export function buildUgcInvoiceSubject(i: UgcInvoiceDraftInput): string {
  const doc = effectiveInvoiceDocumentNumber(
    i.aircraftId,
    i.issuedDateIso,
    i.invoiceDocumentNumber
  );
  return ugcInvoiceEmailSubject(i.aircraftId, doc, i.periodLabel);
}

/** Subject line for mailto (does not need dollar totals). */
export function ugcInvoiceEmailSubject(
  aircraftId: string,
  invoiceDocumentNumber: string,
  periodLabel: string
): string {
  const doc = invoiceDocumentNumber.trim();
  const tail = doc ? ` — ${doc}` : "";
  return `${aircraftId} time-share reimbursement${tail} — ${periodLabel}`;
}
